import {
  DataSet,
  FormulaPropertyDescriptor,
  PropertyDescriptor,
  PropertyDescriptorType,
  Workbook,
} from "../Workbook";
import { parse } from "../../parsers/formula";
import {
  arrayToDict,
  dictionaryKeyMap,
  dictionaryValueMap,
} from "../../utils/dictionaryUtils";
import { Workbook as WorkbookV1 } from "./Workbookv1_1";
import { uuid } from "../../utils/uuid";

type LegacyWorkbook = WorkbookV1;
export function upgradeWorkbook(
  workbook: Workbook | LegacyWorkbook,
  targetVersion: string
): Workbook {
  const [targetMajor, targetMinor] = parseVersion(targetVersion);
  const [sourceMajor, sourceMinor] = parseVersion(workbook.version);
  if (sourceMajor === targetMajor) {
    // We can upgrade without issues
    workbook.version = constructVersion(targetMajor, targetMinor);
    return workbook as Workbook;
  }
  if (sourceMajor in converters) {
    return upgradeWorkbook(converters[sourceMajor](workbook), targetVersion);
  }
  throw new Error(
    `Unable to convert workbook from version ${workbook.version} to ${targetVersion}`
  );
}

function parseVersion(version: string): string[] {
  return version.split(".");
}

function constructVersion(major: string, minor: string): string {
  return `${major}.${minor}`;
}

const converters: {
  [majorVersion: string]: (workbook: Workbook | LegacyWorkbook) => Workbook;
} = {};
converters["1"] = (aWorkbook: Workbook | LegacyWorkbook) => {
  const oldWorkbook = aWorkbook as WorkbookV1;
  // all column ids in formula expressions are represented as key rather than name
  function convertDescriptor(
    newDescriptor: PropertyDescriptor,
    nameToKey: { [name: string]: string }
  ) {
    if (newDescriptor.type === PropertyDescriptorType.Formula) {
      const expression = (newDescriptor as FormulaPropertyDescriptor)
        .expression;
      const nameToKeyMap = nameToKey;
      (newDescriptor as FormulaPropertyDescriptor).expression = parse(
        expression,
        {
          type: "to_property_key",
          nameToKey: nameToKeyMap,
        }
      );
    }
    return newDescriptor;
  }
  const workbook: Workbook = {
    operational: {
      selectedView:
        oldWorkbook.data.tabs[oldWorkbook.operational.selectedWorksheet],
      nextSheetNumber: oldWorkbook.operational.nextWorksheetId,
    },
    data: {
      views: dictionaryValueMap(oldWorkbook.data.worksheets, (worksheet) => {
        return {
          config: worksheet.view,
          name: worksheet.name,
          id: worksheet.id,
        };
      }),
      viewOrder: oldWorkbook.data.tabs,
      datasets: dictionaryValueMap(oldWorkbook.data.datasets, (dataset) => {
        const properties = dataset.properties;
        const newDescriptors = dictionaryValueMap(
          dataset.properties.descriptors,
          (descriptor) =>
            convertDescriptor(descriptor, dataset.properties.nameToKey)
        );
        const newProperties = {
          ...properties,
          ...{ descriptors: newDescriptors },
        };
        const naturalOrder: { [key: number]: string } = {};
        Object.keys(dataset.data).forEach((key) => {
          naturalOrder[parseInt(key)] = uuid();
        });
        const newDataset: DataSet = {
          id: dataset.id,
          name: dataset.name,
          data: dictionaryKeyMap(
            dataset.data,
            (key) => naturalOrder[parseInt(key)]
          ),
          properties: newProperties,
          naturalOrder: naturalOrder,
        };
        return newDataset;
      }),
    },
    name: "Untitled",
    id: "empty",
    version: "2.0",
  };
  return workbook;
};
